const STORAGE_KEY_PREFIX = 'edify-assistant-pending-image'

const MAX_NON_PDF_BLOB_BYTES_FOR_LOCAL_STORAGE = Math.floor(1.25 * 1024 * 1024)
const MAX_DATA_URL_CHARS_FOR_LOCAL_STORAGE = 2_600_000

function shouldSkipPersistPendingToLocalStorage(blob: Blob): boolean {
  const t = blob.type?.trim() || ''
  if (t === 'application/pdf' || t === 'application/x-pdf') return true
  if (blob.size > MAX_NON_PDF_BLOB_BYTES_FOR_LOCAL_STORAGE) return true
  return false
}

export function getImageStorageKey(sessionId?: string, userId?: string): string {
  const parts = [STORAGE_KEY_PREFIX]
  if (userId) parts.push(`user-${userId}`)
  if (sessionId) parts.push(sessionId)
  return parts.join('-')
}

export function getPendingImage(sessionId?: string, userId?: string): string | null {
  try {
    const key = getImageStorageKey(sessionId, userId)
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export async function setPendingImageFromBlob(
  blob: Blob,
  sessionId?: string,
  userId?: string
): Promise<void> {
  if (shouldSkipPersistPendingToLocalStorage(blob)) {
    return
  }
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      try {
        const dataUrl = reader.result as string
        if (
          typeof dataUrl !== 'string' ||
          dataUrl.length > MAX_DATA_URL_CHARS_FOR_LOCAL_STORAGE
        ) {
          resolve()
          return
        }
        const key = getImageStorageKey(sessionId, userId)
        localStorage.setItem(key, dataUrl)
      } catch (e) {
        console.warn('Error saving pending image to localStorage:', e)
      }
      resolve()
    }
    reader.onerror = () => {
      resolve()
    }
    reader.readAsDataURL(blob)
  })
}

export function clearPendingImage(sessionId?: string, userId?: string): void {
  try {
    const key = getImageStorageKey(sessionId, userId)
    localStorage.removeItem(key)
  } catch {
    //
  }
}

export function hasPendingImage(sessionId?: string, userId?: string): boolean {
  return getPendingImage(sessionId, userId) !== null
}

export function mimeFromDataUrl(dataUrl: string): string | null {
  if (!dataUrl.startsWith('data:')) return null
  const m = dataUrl.match(/^data:([^;]+);/)
  return m ? m[1].trim() : null
}

export function getPendingAttachmentMime(
  pendingFile: File | null,
  sessionId?: string,
  userId?: string
): string | null {
  const t = pendingFile?.type?.trim()
  if (t) return t
  const stored = getPendingImage(sessionId, userId)
  if (!stored) return null
  return mimeFromDataUrl(stored)
}

export function pendingAttachmentFallbackCaption(mime: string | null | undefined): string {
  const m = String(mime || '')
  if (m === 'application/pdf') return 'Documentación enviada'
  if (m.startsWith('video/')) return 'Video enviado'
  if (m.startsWith('image/')) return 'Imagen enviada'
  return 'Archivo enviado'
}

function dataURLtoFile(dataurl: string): File {
  const arr = dataurl.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg'
  const isVideo = mime.startsWith('video/')
  const isPdf = mime === 'application/pdf'
  const ext = isVideo ? (mime.includes('webm') ? 'webm' : 'mp4') : isPdf ? 'pdf' : 'jpg'
  const filename = isVideo ? `video.${ext}` : isPdf ? `document.${ext}` : 'image.jpg'
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}

export function getPendingImageAsFile(
  sessionId?: string,
  userId?: string
): File | null {
  const dataUrl = getPendingImage(sessionId, userId)
  if (!dataUrl) return null
  try {
    return dataURLtoFile(dataUrl)
  } catch {
    return null
  }
}
